/*
 * article-drawer-sizing.js
 *
 * Sizing → Drawer Builder handoff for editorial articles. Upgrades the
 * server-rendered form in snippets/article-drawer-sizing.liquid so that one
 * submit opens the localized builder already at the reader's drawer size.
 *
 * WIRE FORMAT: the builder's own hash contract, nothing new —
 * `{L_mm}x{W_mm}` (internal-tools/docs/contracts.md). The builder parses it on
 * load and writes both `.val()` and `data-original` on its length/width
 * inputs, so the size arrives CONFIRMED and the onboarding funnel's size step
 * stays shut (`hasBuildHash()`), which is what makes this a no-re-entry
 * handoff rather than a pre-fill.
 *
 * FAIL CLOSED: a value the builder would reject never becomes a URL. Anything
 * that is not a plain number with at most one decimal place, or that falls
 * outside the builder's own 1–2000 mm gate, blocks navigation and surfaces the
 * builder's own error copy instead.
 *
 * PROGRESSIVE ENHANCEMENT: the markup is a real GET form pointed at the
 * localized builder page. With this script absent, blocked or broken, submit
 * still reaches the builder, but dimensions are not preserved; this script is
 * what provides the validated `#LxW` no-re-entry handoff.
 *
 * ANALYTICS: emits the uniquely named `onboarding_article_sizing_handoff`
 * event. The live onboarding GA4 tag already routes the `onboarding_.*`
 * family, so the action is attributable without a GTM mutation. Supporting
 * source/cta fields remain in the data layer for debugging.
 *
 * The block between the md-sizing-handoff markers is evaluated verbatim by
 * tests/article-drawer-sizing.test.js; keep it free of DOM globals.
 */
(function () {
  'use strict';

  // md-sizing-handoff-start
  // The builder's own accepted range (`newLength < 1 || newLength > 2000` in
  // assets/drawer-builder-legacy-inline.js, and the copy in
  // modu.builder.length_error). Mirrored rather than tightened: a size this
  // form accepts is by definition a size the builder accepts.
  var MIN_MM = 1;
  var MAX_MM = 2000;

  // Reads one millimetre field. Returns a number the builder will accept, or
  // null. Deliberately strict, because the alternative to an error message is
  // a silently wrong drawer:
  //   - every kind of space is dropped first, so the nl/de/fr grouping habit
  //     ("1 200", with a normal, non-breaking or narrow space) resolves to
  //     1200 rather than failing;
  //   - a typed "mm" unit is dropped; any other unit ("41 cm") is rejected,
  //     because guessing a conversion is worse than asking again;
  //   - one comma OR one dot is accepted as the decimal separator, followed by
  //     at most ONE digit. That single-digit limit is what disambiguates the
  //     de/nl thousands separator: "1.200" cannot be read as 1.2 mm, so it
  //     fails closed and the reader retypes, instead of being handed a 1.2 mm
  //     drawer. 0.1 mm is already finer than any tape measure.
  function parseMm(raw) {
    if (raw === null || raw === undefined) return null;
    var text = String(raw).replace(/[\s    ]/g, '');
    text = text.replace(/mm$/i, '');
    if (!/^\d+(?:[.,]\d)?$/.test(text)) return null;
    var value = parseFloat(text.replace(',', '.'));
    if (!isFinite(value)) return null;
    value = Math.round(value * 10) / 10;
    if (value < MIN_MM || value > MAX_MM) return null;
    return value;
  }

  // Hash dialect: "preserved decimal with trailing zeros trimmed", which is
  // what String() already does for a value rounded to 0.1 mm.
  function formatMm(value) {
    return String(Math.round(value * 10) / 10);
  }

  function buildHash(lengthMm, widthMm) {
    if (typeof lengthMm !== 'number' || typeof widthMm !== 'number') return null;
    if (!isFinite(lengthMm) || !isFinite(widthMm)) return null;
    if (lengthMm < MIN_MM || lengthMm > MAX_MM) return null;
    if (widthMm < MIN_MM || widthMm > MAX_MM) return null;
    // Length is the FIRST token: the builder assigns dimensions[0] to
    // input[name="length"].
    return formatMm(lengthMm) + 'x' + formatMm(widthMm);
  }

  // `action` is the localized builder path rendered by Liquid. Any fragment
  // already on it is replaced, never appended to.
  function buildHref(action, lengthMm, widthMm) {
    var hash = buildHash(lengthMm, widthMm);
    if (!hash) return null;
    var base = String(action === null || action === undefined ? '' : action).split('#')[0];
    if (!base) return null;
    return base + '#' + hash;
  }

  var mdSizingHandoff = {
    MIN_MM: MIN_MM,
    MAX_MM: MAX_MM,
    parseMm: parseMm,
    formatMm: formatMm,
    buildHash: buildHash,
    buildHref: buildHref
  };

  // Wires one rendered form. Every effect goes through `form` and `win`, so
  // tests drive it with a stub instead of a DOM library. Returns the submit
  // handler, or null when the markup is not the shape this script upgrades —
  // in which case the plain GET form is left exactly as the server sent it.
  function mdSizingHandoffAttach(form, win) {
    if (!form || !win) return null;
    if (form.getAttribute('data-drawer-sizing-ready') === 'true') return null;

    var lengthInput = form.querySelector('[data-drawer-sizing-length]');
    var widthInput = form.querySelector('[data-drawer-sizing-width]');
    var action = form.getAttribute('action');
    if (!lengthInput || !widthInput || !action) return null;

    var fields = [
      { input: lengthInput, error: form.querySelector('[data-drawer-sizing-error="length"]') },
      { input: widthInput, error: form.querySelector('[data-drawer-sizing-error="width"]') }
    ];
    var status = form.querySelector('[data-drawer-sizing-status]');
    var summary = form.getAttribute('data-sizing-summary') || '';
    var navigating = false;

    form.setAttribute('data-drawer-sizing-ready', 'true');

    function setFieldState(field, invalid) {
      field.input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (!field.error) return;
      if (invalid) field.error.removeAttribute('hidden');
      else field.error.setAttribute('hidden', 'hidden');
    }

    // A live region only speaks on change, so an identical message after a
    // second failed submit has to be cleared and rewritten in a later task.
    function announce(text) {
      if (!status) return;
      status.textContent = '';
      if (!text) return;
      if (typeof win.setTimeout === 'function') {
        win.setTimeout(function () {
          status.textContent = text;
        }, 50);
      } else {
        status.textContent = text;
      }
    }

    function pushRouteEvent(lengthMm, widthMm) {
      try {
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push({
          event: 'onboarding_article_sizing_handoff',
          source: 'article_sizing',
          cta: 'sizing_handoff',
          destination: String(action).split('#')[0],
          // The live onboarding GA4 tag already maps these two data-layer
          // variable names into event parameters. Keep the names aligned so
          // the routed event carries the accepted dimensions without a GTM
          // mutation.
          onboarding_drawer_length: lengthMm,
          onboarding_drawer_width: widthMm
        });
      } catch (error) {
        /* analytics must never break the handoff */
      }
    }

    function block(event, firstInvalid) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      announce(summary);
      if (firstInvalid && typeof firstInvalid.input.focus === 'function') firstInvalid.input.focus();
      return false;
    }

    function onSubmit(event) {
      if (navigating) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        return false;
      }

      var values = [parseMm(fields[0].input.value), parseMm(fields[1].input.value)];
      var firstInvalid = null;
      for (var index = 0; index < fields.length; index += 1) {
        var valid = values[index] !== null;
        // aria-invalid carries the field's own state; the shared summary in
        // the live region covers the case where focus never moves.
        setFieldState(fields[index], !valid);
        if (!valid && !firstInvalid) firstInvalid = fields[index];
      }
      if (firstInvalid) return block(event, firstInvalid);

      var href = buildHref(action, values[0], values[1]);
      // Unreachable while parseMm and buildHash share one range, and still
      // blocking rather than falling through to a plain submit if they drift.
      if (!href) return block(event, fields[0]);

      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      announce('');
      navigating = true;
      pushRouteEvent(values[0], values[1]);
      win.location.assign(href);
      return true;
    }

    form.addEventListener('submit', onSubmit);
    // Back from the builder restores this page from the bfcache with the
    // closure intact, so the in-flight guard has to be released or the reader
    // can never hand off a second time.
    if (typeof win.addEventListener === 'function') {
      win.addEventListener('pageshow', function () {
        navigating = false;
      });
    }
    return onSubmit;
  }
  // md-sizing-handoff-end

  function init() {
    var form = document.querySelector('[data-drawer-sizing]');
    if (!form) return;
    mdSizingHandoffAttach(form, window);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
